import axios from 'axios';
import { API_BASE } from './api';

const LOGGER_FLAG = '__API_LOGGER_INSTALLED__';

const normalizeUrl = (input) => {
  if (!input) return '';
  if (typeof input === 'string') return input;
  if (input.url) return input.url;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return String(input);
};

const shouldLog = (url) => {
  if (!API_BASE) return false;
  return typeof url === 'string' && url.includes(API_BASE);
};

const setupApiLogging = () => {
  const originalFetch = typeof window !== 'undefined' ? window.fetch?.bind(window) : null;

  if (originalFetch) {
    window.fetch = async (...args) => {
      const [input, init = {}] = args;
      const url = normalizeUrl(input);
      const method = (init.method || 'GET').toUpperCase();
      const loggable = shouldLog(url);
      const start = typeof performance !== 'undefined' ? performance.now() : null;

      if (loggable) {
        const payload = init?.body ? { body: init.body } : undefined;
        console.log(`[API] fetch ${method} ${url}`, payload);
      }

      try {
        const response = await originalFetch(...args);
        if (loggable) {
          const duration = start ? Math.round(performance.now() - start) : null;
          const timingSuffix = duration ? ` (${duration}ms)` : '';
          console.log(`[API] fetch response ${response.status} ${url}${timingSuffix}`);
        }
        return response;
      } catch (error) {
        if (loggable) {
          console.error(`[API] fetch error ${method} ${url}`, error);
        }
        throw error;
      }
    };
  }

  axios.interceptors.request.use(
    (config) => {
      const url = normalizeUrl(config.url);
      if (shouldLog(url)) {
        const method = (config.method || 'GET').toUpperCase();
        const payload = config.data || config.params ? { data: config.data, params: config.params } : undefined;
        console.log(`[API] axios ${method} ${url}`, payload);
      }
      return config;
    },
    (error) => {
      console.error('[API] axios request error', error);
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response) => {
      const url = normalizeUrl(response.config?.url);
      if (shouldLog(url)) {
        console.log(`[API] axios response ${response.status} ${url}`, response.data);
      }
      return response;
    },
    (error) => {
      const url = normalizeUrl(error.config?.url || error.response?.config?.url);
      if (shouldLog(url)) {
        const status = error.response?.status ?? 'NO_STATUS';
        const payload = error.response?.data || error.message;
        console.error(`[API] axios response error ${status} ${url}`, payload);
      }
      return Promise.reject(error);
    }
  );
};

if (typeof window !== 'undefined' && !window[LOGGER_FLAG]) {
  window[LOGGER_FLAG] = true;
  setupApiLogging();
}
